import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ReceiptModal from "@/components/ReceiptModal";
import { Receipt, ChevronRight, Search, Undo2, Minus, Plus, Printer, X } from "lucide-react";

const badge = {
  tunai: "bg-emerald-500/15 text-emerald-600",
  transfer: "bg-blue-500/15 text-blue-400",
  qris: "bg-purple-500/15 text-purple-400",
  split: "bg-amber-500/15 text-amber-600",
};

const rkey = (x) => `${x.product_id}|${x.name}|${x.price}`;

// remaining qty per line after existing refunds
const remainingPerLine = (tx) => {
  const pool = {};
  (tx.refunds || []).forEach((r) => r.items.forEach((it) => { const k = rkey(it); pool[k] = (pool[k] || 0) + it.qty; }));
  return tx.items.map((it) => {
    const k = rkey(it);
    const used = Math.min(it.qty, pool[k] || 0);
    pool[k] = (pool[k] || 0) - used;
    return it.qty - used;
  });
};

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [refundQty, setRefundQty] = useState({});
  const [refunding, setRefunding] = useState(false);
  const [reprint, setReprint] = useState(null);

  const load = () =>
    api.get("/transactions?limit=300").then((r) => setTxs(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return txs;
    return txs.filter((t) => t.invoice_no.toLowerCase().includes(q));
  }, [txs, search]);

  const openTx = (t) => { setSelected(t); setRefundQty({}); };

  const remaining = selected ? remainingPerLine(selected) : [];
  const refundAmount = selected
    ? selected.items.reduce((s, it, i) => s + (refundQty[i] || 0) * it.price, 0)
    : 0;

  const setQ = (i, v) => setRefundQty((m) => ({ ...m, [i]: Math.max(0, Math.min(remaining[i], v)) }));
  const setAll = () => { const m = {}; selected.items.forEach((_, i) => { m[i] = remaining[i]; }); setRefundQty(m); };
  const setHalf = () => { const m = {}; selected.items.forEach((_, i) => { m[i] = Math.floor(remaining[i] / 2); }); setRefundQty(m); };

  const doRefund = async () => {
    const items = selected.items
      .map((it, i) => ({ product_id: it.product_id, name: it.name, price: it.price, pieces: it.pieces || 1, qty: refundQty[i] || 0 }))
      .filter((x) => x.qty > 0);
    if (items.length === 0) return toast.error("Pilih jumlah barang yang direfund");
    setRefunding(true);
    try {
      const { data } = await api.post(`/transactions/${selected.id}/refund`, { items });
      toast.success(`Refund ${rupiah(items.reduce((s, x) => s + x.qty * x.price, 0))} berhasil, stok dikembalikan`);
      setSelected(data);
      setRefundQty({});
      setTxs((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRefunding(false);
    }
  };

  const doReprint = (tx) => {
    const rem = remainingPerLine(tx);
    const items = tx.items.map((it, i) => ({ ...it, qty: rem[i] })).filter((it) => it.qty > 0);
    const subtotal = items.reduce((s, it) => s + it.qty * it.price, 0);
    const method = tx.payment_type === "split" ? "tunai" : tx.payment_type;
    setReprint({
      id: tx.id,
      invoice_no: `${tx.invoice_no} (REVISI)`,
      items,
      subtotal,
      discount: 0,
      total: subtotal,
      payments: [{ method, amount: subtotal }],
      paid: subtotal,
      change: 0,
      created_at: tx.created_at,
      cashier: tx.cashier,
    });
  };

  const anyRefundSelected = Object.values(refundQty).some((v) => v > 0);
  const canRefundMore = remaining.some((r) => r > 0);

  return (
    <>
      <PageHeader title="Transaksi" subtitle={`${txs.length} transaksi terakhir`}>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="tx-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari No. Invoice…"
            className="pl-10 bg-[#F7F9FB] border-slate-300 h-10"
          />
        </div>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="transactions-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">No. Invoice</th>
                <th className="text-left p-3 hidden md:table-cell">Waktu</th>
                <th className="text-left p-3 hidden sm:table-cell">Kasir</th>
                <th className="text-center p-3">Bayar</th>
                <th className="text-right p-3">Total</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`} onClick={() => openTx(t)} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <td className="p-3 font-mono">
                    {t.invoice_no}
                    {t.refund_total > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 font-sans">REFUND</span>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-slate-500">{new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-3 hidden sm:table-cell text-slate-500">{t.cashier}</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${badge[t.payment_type]}`}>{t.payment_type}</span></td>
                  <td className="p-3 text-right font-mono font-bold text-emerald-600">
                    {rupiah(t.net_total != null ? t.net_total : t.total)}
                    {t.refund_total > 0 && <div className="text-[10px] text-slate-500 line-through">{rupiah(t.total)}</div>}
                  </td>
                  <td className="p-3 text-slate-500"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-slate-500"><Receipt className="h-8 w-8 mx-auto mb-2" /> Tidak ada transaksi</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div data-testid="tx-detail-modal" className="bg-[#F7F9FB] border border-slate-300 rounded-xl max-w-md w-full p-5 animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-3">
              <div className="font-heading font-bold text-lg">{selected.invoice_no}</div>
              <div className="text-xs text-slate-500">{new Date(selected.created_at).toLocaleString("id-ID")} · {selected.cashier}</div>
            </div>

            <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
              {selected.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-slate-600 truncate">{it.name} <span className="text-slate-500">x{it.qty}</span></div>
                    <div className="text-[11px] text-slate-500">Sisa bisa refund: {remaining[i]} · {rupiah(it.price)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`refund-minus-${i}`} disabled={!(refundQty[i] > 0)} onClick={() => setQ(i, (refundQty[i] || 0) - 1)} className="h-7 w-7 rounded bg-[#F7F9FB] border border-slate-300 flex items-center justify-center disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
                    <span data-testid={`refund-qty-${i}`} className="w-7 text-center font-mono text-sm">{refundQty[i] || 0}</span>
                    <button data-testid={`refund-plus-${i}`} disabled={(refundQty[i] || 0) >= remaining[i]} onClick={() => setQ(i, (refundQty[i] || 0) + 1)} className="h-7 w-7 rounded bg-[#F7F9FB] border border-slate-300 flex items-center justify-center disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-slate-300 mt-3 pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500"><span>Total awal</span><span className="font-mono">{rupiah(selected.total)}</span></div>
              {selected.refund_total > 0 && <div className="flex justify-between text-red-600"><span>Sudah direfund</span><span className="font-mono">-{rupiah(selected.refund_total)}</span></div>}
              <div className="flex justify-between font-bold text-emerald-600"><span>Total bersih</span><span className="font-mono">{rupiah(selected.net_total != null ? selected.net_total : selected.total)}</span></div>
            </div>

            {canRefundMore && (
              <div className="mt-4 rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Undo2 className="h-4 w-4 text-red-600" /> Refund / Batalkan Barang</span>
                  <div className="flex gap-1">
                    <button data-testid="refund-half-btn" onClick={setHalf} className="text-[11px] px-2 h-7 rounded bg-[#F7F9FB] border border-slate-300 hover:border-amber-500">Separuh</button>
                    <button data-testid="refund-all-btn" onClick={setAll} className="text-[11px] px-2 h-7 rounded bg-[#F7F9FB] border border-slate-300 hover:border-red-500">Semua</button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Jumlah refund</span>
                  <span data-testid="refund-amount" className="font-mono font-bold text-red-600">{rupiah(refundAmount)}</span>
                </div>
                <WriteButton data-testid="confirm-refund-btn" onClick={doRefund} disabled={!anyRefundSelected || refunding} className="w-full h-11 bg-red-500 hover:bg-red-600 text-white font-bold gap-2 disabled:opacity-40">
                  <Undo2 className="h-4 w-4" /> Proses Refund (stok dikembalikan)
                </WriteButton>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button data-testid="reprint-btn" variant="outline" onClick={() => doReprint(selected)} className="h-11 border-slate-300 bg-transparent hover:bg-[#F7F9FB] gap-2">
                <Printer className="h-4 w-4" /> Cetak Ulang{selected.refund_total > 0 ? " (Revisi)" : ""}
              </Button>
              <Button data-testid="close-tx-btn" variant="ghost" onClick={() => setSelected(null)} className="h-11 gap-2 text-slate-500 hover:text-slate-800">
                <X className="h-4 w-4" /> Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      <ReceiptModal tx={reprint} cashier={reprint?.cashier} onClose={() => setReprint(null)} />
    </>
  );
}
